import React, { useEffect, useMemo, useCallback } from "react";
import Moment from "react-moment";
import { getCurrentUser } from "../../app/store/auth";
import { useDispatch, useSelector } from "react-redux";
import { getFieldData } from "../../Services/appService";
import { setCurrentPageTitle, setCurrentRoute } from "../../app/store/layoutUI";
import { getComponentUserStatus } from "../../app/store/tools";
import { useGetUserDetailsQuery } from "../../app/store/authApiSlice";
import { useLoadBackgroundChecksQuery } from "../../app/store/candidateApiSlice";
import { useUpdateComponentsUserStatusMutation } from "../../app/store/toolsApiSlice";

const BACKGROUND_CHECK_STYLES = {
  card: {
    borderRadius: "20px",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    backgroundColor: "#ffffff",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
    overflow: "hidden"
  },
  cardHeader: {
    padding: "18px 22px",
    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
    background: "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)"
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap"
  },
  titleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0
  },
  titleIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "10px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    color: "#2563eb",
    fontSize: "14px",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    boxShadow: "0 4px 12px rgba(37, 99, 235, 0.06)",
    flexShrink: 0
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: 1.25,
    color: "#0f172a",
    margin: 0
  },
  subtitle: {
    fontSize: "12.5px",
    lineHeight: 1.5,
    color: "#64748b",
    margin: "2px 0 0 0",
    fontWeight: 600
  },
  refreshButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  cardBody: {
    padding: "0"
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "14px",
    padding: "20px 22px"
  },
  statCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    backgroundColor: "#ffffff",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    borderRadius: "16px",
    padding: "16px",
    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.03)",
    transition: "all 0.2s ease"
  },
  statIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "10px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#eff6ff",
    color: "#2563eb",
    fontSize: "14px",
    flexShrink: 0
  },
  statContent: {
    minWidth: 0
  },
  statLabel: {
    margin: "0 0 4px 0",
    color: "#64748b",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase"
  },
  statValue: {
    margin: 0,
    color: "#0f172a",
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: 1
  },
  tableWrap: {
    overflowX: "auto",
    backgroundColor: "#ffffff"
  },
  table: {
    width: "100%",
    marginBottom: 0,
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "13px",
    color: "#334155"
  },
  tableHeadCell: {
    borderTop: "none",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
    padding: "14px 16px"
  },
  cell: {
    verticalAlign: "top",
    padding: "16px 18px",
    borderBottom: "1px solid rgba(241, 245, 249, 1)",
    background: "#ffffff",
    fontWeight: 600
  },
  idValue: {
    fontSize: "13.5px",
    fontWeight: 600,
    color: "#0f172a",
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  typeText: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#0f172a",
    lineHeight: 1.4
  },
  datePrimary: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#0f172a",
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  dateSecondary: {
    fontSize: "11.5px",
    color: "#64748b",
    fontWeight: 600
  },
  commentText: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#475569",
    marginBottom: "4px",
    whiteSpace: "pre-line",
    fontWeight: 600
  },
  metaText: {
    fontSize: "11.5px",
    fontWeight: 600,
    color: "#64748b"
  },
  emptyState: {
    padding: "42px 24px",
    textAlign: "center",
    backgroundColor: "#ffffff",
    color: "#64748b",
    fontSize: "14px",
    fontWeight: 600
  },
  emptyTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#0f172a",
    marginBottom: "6px"
  },
  emptyText: {
    fontSize: "13px",
    lineHeight: 1.55,
    color: "#64748b",
    margin: 0,
    fontWeight: 600
  }
};

const getStatusConfig = (status) => {
  if (status === true) {
    return {
      label: "Passed",
      style: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 12px",
        borderRadius: "8px",
        backgroundColor: "#f0fdf4",
        border: "1px solid rgba(187, 247, 208, 1)",
        color: "#166534",
        fontSize: "12.5px",
        fontWeight: 600,
        whiteSpace: "nowrap"
      },
      icon: "fa-check-circle"
    };
  }

  if (status === false) {
    return {
      label: "Failed",
      style: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 12px",
        borderRadius: "8px",
        backgroundColor: "#fef2f2",
        border: "1px solid rgba(254, 202, 202, 1)",
        color: "#991b1b",
        fontSize: "12.5px",
        fontWeight: 600,
        whiteSpace: "nowrap"
      },
      icon: "fa-times-circle"
    };
  }

  return {
    label: "In Process",
    style: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "6px 12px",
      borderRadius: "8px",
      backgroundColor: "#fffbeb",
      border: "1px solid rgba(253, 230, 138, 1)",
      color: "#b45309",
      fontSize: "12.5px",
      fontWeight: 600,
      whiteSpace: "nowrap"
    },
    icon: "fa-clock-o"
  };
};

const ROUTE = [
  { name: "Task Management", url: "" },
  { name: "Onboarding Steps", url: "" },
];

export default function BackgroundChecks() {
  const dispatch = useDispatch();
  const currentUser = getCurrentUser();
  const currentUserId = currentUser?.id;

  const { data: userDetails } = useGetUserDetailsQuery(currentUserId, {
    skip: !currentUserId,
  });
  const { data: backgroundChecks, isLoading: isLoadingBackgroundChecks, isFetching: isFetchingBackgroundChecks, refetch: refetchBackgroundChecks } = useLoadBackgroundChecksQuery(currentUserId, {
    skip: !currentUserId,
  });

  const [updateComponentsUserStatus] = useUpdateComponentsUserStatusMutation();
  const componentUserStatusSelector = useSelector(getComponentUserStatus);

  const entityType = useMemo(() => {
    if (!userDetails) return "";
    return (
      getFieldData(
        userDetails.performerData,
        "candidateonboardingform",
        "CandidateEntity"
      ) || ""
    );
  }, [userDetails]);

  const count = entityType !== "C2C Basis" ? "4" : "3";

  useEffect(() => {
    dispatch(setCurrentPageTitle(`STEP-${count} : Background Check`));
    dispatch(setCurrentRoute(ROUTE));
  }, [dispatch, count]);

  const bcChecks = useMemo(() => {
    if (!backgroundChecks || backgroundChecks.length === 0) return [];
    return backgroundChecks.filter((x) => x.sendStatus === true);
  }, [backgroundChecks]);

  const { bcItems, lastCiid } = useMemo(() => {
    if (bcChecks.length === 0) return { bcItems: "", lastCiid: 0 };
    const items = bcChecks.map((x) => x.id).join(",");
    const ciid = bcChecks[bcChecks.length - 1].ciid;
    return { bcItems: items, lastCiid: ciid };
  }, [bcChecks]);

  useEffect(() => {
    if (bcItems !== "" && currentUserId) {
      const comp = componentUserStatusSelector.filter((x) => x.compId === 26);
      let updCompItems = "";
      if (comp.length > 0) {
        updCompItems = comp[0].compItems;
      }
      if (bcItems !== updCompItems) {
        updateComponentsUserStatus({
          compName: "Background Checks",
          performerId: currentUserId,
          compItems: bcItems,
          ciid: lastCiid,
          wiid: 0,
          iiid: 0,
          compId: 26,
        });
      }
    }
  }, [bcItems, lastCiid, componentUserStatusSelector, currentUserId, updateComponentsUserStatus]);

  const summary = useMemo(() => {
    const total = bcChecks.length;
    const passed = bcChecks.filter((item) => item.status === true).length;
    const failed = bcChecks.filter((item) => item.status === false).length;
    const inProcess = total - passed - failed;

    return { total, passed, failed, inProcess };
  }, [bcChecks]);

  const handleRefresh = useCallback(() => {
    refetchBackgroundChecks();
  }, [refetchBackgroundChecks]);

  return (
    <React.Fragment>
      <div className="form-group mt-2">
        <div style={BACKGROUND_CHECK_STYLES.card}>
          <div style={BACKGROUND_CHECK_STYLES.cardHeader}>
            <div style={BACKGROUND_CHECK_STYLES.headerRow}>
              <div style={BACKGROUND_CHECK_STYLES.titleWrap}>
                <span style={BACKGROUND_CHECK_STYLES.titleIcon}>
                  <i className="fa fa-shield" aria-hidden="true"></i>
                </span>
                <div>
                  <h1 style={BACKGROUND_CHECK_STYLES.title}>Background Check</h1>
                  <p style={BACKGROUND_CHECK_STYLES.subtitle}>
                    Track screening checks issued for your onboarding process with real-time updates and reviewer feedback.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm"
                style={BACKGROUND_CHECK_STYLES.refreshButton}
                onClick={handleRefresh}
              >
                <i className="fa fa-refresh" aria-hidden="true"></i> Refresh
              </button>
            </div>
          </div>
          <div style={BACKGROUND_CHECK_STYLES.cardBody}>
            <div style={BACKGROUND_CHECK_STYLES.statsRow}>
              <div style={BACKGROUND_CHECK_STYLES.statCard}>
                <div style={BACKGROUND_CHECK_STYLES.statIcon}>
                  <i className="fa fa-paper-plane-o" aria-hidden="true"></i>
                </div>
                <div style={BACKGROUND_CHECK_STYLES.statContent}>
                  <div style={BACKGROUND_CHECK_STYLES.statLabel}>Checks Sent</div>
                  <div style={BACKGROUND_CHECK_STYLES.statValue}>{summary.total}</div>
                </div>
              </div>
              <div style={BACKGROUND_CHECK_STYLES.statCard}>
                <div style={BACKGROUND_CHECK_STYLES.statIcon}>
                  <i className="fa fa-check-circle-o" aria-hidden="true"></i>
                </div>
                <div style={BACKGROUND_CHECK_STYLES.statContent}>
                  <div style={BACKGROUND_CHECK_STYLES.statLabel}>Passed</div>
                  <div style={BACKGROUND_CHECK_STYLES.statValue}>{summary.passed}</div>
                </div>
              </div>
              <div style={BACKGROUND_CHECK_STYLES.statCard}>
                <div style={BACKGROUND_CHECK_STYLES.statIcon}>
                  <i className="fa fa-times-circle-o" aria-hidden="true"></i>
                </div>
                <div style={BACKGROUND_CHECK_STYLES.statContent}>
                  <div style={BACKGROUND_CHECK_STYLES.statLabel}>Failed</div>
                  <div style={BACKGROUND_CHECK_STYLES.statValue}>{summary.failed}</div>
                </div>
              </div>
              <div style={BACKGROUND_CHECK_STYLES.statCard}>
                <div style={BACKGROUND_CHECK_STYLES.statIcon}>
                  <i className="fa fa-clock-o" aria-hidden="true"></i>
                </div>
                <div style={BACKGROUND_CHECK_STYLES.statContent}>
                  <div style={BACKGROUND_CHECK_STYLES.statLabel}>In Review</div>
                  <div style={BACKGROUND_CHECK_STYLES.statValue}>{summary.inProcess}</div>
                </div>
              </div>
            </div>
            <div className="table-responsive" style={BACKGROUND_CHECK_STYLES.tableWrap}>
              <table className="table align-items-center table-hover mb-0" style={BACKGROUND_CHECK_STYLES.table}>
                <thead>
                  <tr>
                    <th style={BACKGROUND_CHECK_STYLES.tableHeadCell}># ID</th>
                    <th style={BACKGROUND_CHECK_STYLES.tableHeadCell}>Date & Time</th>
                    <th style={BACKGROUND_CHECK_STYLES.tableHeadCell}>Verification Type</th>
                    <th style={BACKGROUND_CHECK_STYLES.tableHeadCell}>Status</th>
                    <th style={BACKGROUND_CHECK_STYLES.tableHeadCell}>Reviewer Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingBackgroundChecks || (isFetchingBackgroundChecks && bcChecks.length === 0) ? (
                    <tr>
                      <td colSpan="5" style={{ padding: "44px 20px", textAlign: "center", background: "#ffffff" }}>
                        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                          <i className="fa fa-circle-o-notch fa-spin" style={{ fontSize: "26px", color: "#2563eb" }}></i>
                          <div style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a" }}>Loading background checks...</div>
                          <div style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>Please wait while we retrieve background check records.</div>
                        </div>
                      </td>
                    </tr>
                  ) : bcChecks.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ padding: "48px 20px", textAlign: "center", background: "#ffffff" }}>
                        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "12px", maxWidth: "380px", margin: "0 auto" }}>
                          <div style={{ width: "48px", height: "48px", borderRadius: "14px", background: "#eff6ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", border: "1px solid rgba(191, 219, 254, 0.9)" }}>
                            <i className="fa fa-shield" aria-hidden="true"></i>
                          </div>
                          <div>
                            <div style={{ fontSize: "15px", fontWeight: 600, color: "#0f172a", marginBottom: "4px" }}>No Background Checks Found</div>
                            <div style={{ fontSize: "13px", fontWeight: 600, color: "#64748b", lineHeight: 1.5 }}>There are no background check records available for this candidate at this time.</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    bcChecks.map((item, key) => {
                      const statusConfig = getStatusConfig(item.status);
                      return (
                        <tr key={item.id || key}>
                          <td style={BACKGROUND_CHECK_STYLES.cell}>
                            <span style={BACKGROUND_CHECK_STYLES.idValue}>
                              # {key + 1}
                            </span>
                          </td>
                          <td style={BACKGROUND_CHECK_STYLES.cell}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                              <div style={BACKGROUND_CHECK_STYLES.datePrimary}>
                                <i className="fa fa-calendar-o" aria-hidden="true" style={{ fontSize: "12px", color: "#2563eb" }}></i>
                                {item.updatedDateTime ? <Moment format="MMM Do, YYYY">{item.updatedDateTime}</Moment> : "--"}
                              </div>
                              <div style={BACKGROUND_CHECK_STYLES.dateSecondary}>
                                {item.updatedDateTime ? <Moment format="h:mm a">{item.updatedDateTime}</Moment> : "--"}
                              </div>
                            </div>
                          </td>
                          <td style={BACKGROUND_CHECK_STYLES.cell}>
                            <div style={BACKGROUND_CHECK_STYLES.typeText}>{item.name}</div>
                          </td>
                          <td style={BACKGROUND_CHECK_STYLES.cell}>
                            <span style={statusConfig.style}>
                              <i className={`fa ${statusConfig.icon}`} aria-hidden="true"></i>
                              {statusConfig.label}
                            </span>
                          </td>
                          <td style={BACKGROUND_CHECK_STYLES.cell}>
                            <div style={BACKGROUND_CHECK_STYLES.commentText}>
                              {item.instructionNotes || "No reviewer comments available yet."}
                            </div>
                            {item.updatedDateTime && (
                              <div style={BACKGROUND_CHECK_STYLES.metaText}>
                                Updated: <Moment format="MMM Do, YYYY, h:mm a">{item.updatedDateTime}</Moment>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
